import { ChangeDetectionStrategy, Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IBlockForSelect } from '../../../../utils/blocks-helper';

/**
 * The block-catalog modal opened by any "+" gap (see add-block-v2). A single
 * shared instance owned by preview-v2, fixed to the viewport at the same spot
 * (just right of the sidebar, just below the navbars) regardless of which "+"
 * triggered it - matches the mockup's own .app-shell > .block-library
 * .popup-library (also position:fixed, also anchored next to its sidebar
 * rather than the clicked button).
 *
 * Insertion logic (spliceIndex computation, blockSelectEvent shape) mirrors
 * app-add-block's emitBlockSelect() 1:1 so base-template-editor's
 * handleBlockChangeEvent()/handleBlockClick() keep working unchanged.
 */
@Component({
  selector: 'app-add-block-modal-v2',
  imports: [CommonModule],
  templateUrl: './add-block-modal-v2.component.html',
  styleUrl: './add-block-modal-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddBlockModalV2Component {
  /** The block to insert after (the gap's own blockData) - non-null while open. */
  @Input() blockData: any = null;
  @Input() selectedBlocks: any[] = [];
  @Input() blockSelect: IBlockForSelect[] = [];

  @Output() closeModal = new EventEmitter<void>();
  @Output() blockSelectEvent = new EventEmitter<any>();

  protected hoveredBlock: IBlockForSelect | null = null;
  /** Top offset (px, relative to .cr-add-block-modal) for the hover popover -
   *  captured from the hovered row's own position since it renders outside
   *  the scrolling row list (see the template comment on why). */
  protected hoveredRowOffsetTop = 0;

  // Placeholder copy/art until real per-block descriptions and preview
  // thumbnails are supplied - see conversation with the design owner.
  protected readonly placeholderDescription =
    'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua';

  /** Real per-block copy for the ones with a finished preview - see
   *  hasRealPreview(). Everything else keeps the placeholder text above. */
  private readonly realDescriptions: Record<string, string> = {
    'main-banner': 'Displays a full-width banner image for key messages or visual content throughout the email.',
    'paragraph-cta': 'Text section with a call-to-action button, suitable for introductions or any content section.',
    heading: 'Single or multi-line text headline.',
    'only-intro': 'Text section suitable for introductions or any content section.',
    'only-cta': 'Adds a standalone call-to-action button wherever an additional action is needed.',
    'property-description': 'Displays a detailed description of a selected property.',
    'x2-prop-rec': 'Presents two properties with their destination names side by side.',
    counter: 'Displays a countdown to create urgency around time-sensitive offers.',
    event: 'Large inspirational image with supporting description.',
    'property-usp-1x-photo': 'Highlights a single property with one image and its key benefits.',
    'property-usp-2x-photo': 'Showcases two property highlights with images and key selling points.',
    'x2-experiences': 'Big headline with two inspirational images with individual headings and descriptions.',
    inspiration: 'Two inspirational images with individual descriptions.',
    'x3-experiences': 'Three inspirational images with individual descriptions.',
    'only-2-photos': 'Two images side by side, no heading or call-to-action.',
    voucher: 'Displays a voucher with promotional offer and supporting information.',
    'gift-voucher': 'Promotes gift vouchers with a dedicated call-to-action.',
    'experience-concierge': 'Gift voucher presentation without a call-to-action button.',
    moments: 'Displays a full-width image, ideal for seasonal or promotional moments.',
    reservation: 'Displays reservation details and contact information.',
    'check-in': 'Content block intended for CRM-specific communication.',
    disclaimer: 'Legal or informational text displayed at the end of the email.',
    'valamar-app': 'Promotes the Valamar App with key benefits and download options.',
    'x2-prop-rec-onp': 'Compares two properties with old and new pricing information.',
    reviews: 'Displays guest reviews and testimonials.',
    'zigzag-heading-and-cta': 'Alternating image and text layout with a heading and call-to-action.',
    'zigzag-heading-and-alt-cta': 'Alternating content layout with a heading and an alternative CTA style.',
    'zigzag-no-heading-no-button': 'Alternating image and text layout without a heading or CTA.',
  };

  /** Dummy copy for the large preview modal's rendered body - real block
   *  types only (see hasRealPreview()), sourced from the mockup's own sample
   *  content so the preview reads like a real message, not lorem ipsum. */
  protected readonly introCtaPreviewText =
    'Arba Resort, Valamar Collection smješten je na zelenom poluotoku Kalifront i nudi moderan obiteljski odmor uz plaže, bazene i brojne aktivnosti.';
  protected readonly introCtaPreviewCtaText = 'Rezervirajte sada';
  protected readonly headlinePreviewText = 'Arba Resort, Valamar Collection';
  protected readonly onlyIntroPreviewText =
    'Arba Resort, Valamar Collection je premium all inclusive obiteljska oaza smještena na zelenom poluotoku Kalifront, na samo 5 km udaljenosti od slikovitog grada Raba. Resort u ponudi ima moderne sobe i ekskluzivne V Level obiteljske suiteove uređene s posebnim naglaskom na udobnosti. Obitelji mogu uživati u šest unutarnjih i vanjskih bazena, prekrasnim plažama i mnoštvu aktivnosti kao što su bicikliranje i trčanje kroz šume poluotoka Kalifront.';
  protected readonly onlyCtaPreviewCtaText = 'Pogledajte ponudu';
  protected readonly propertyDescPreviewTitle = 'Arba Resort, Valamar Collection';
  protected readonly propertyDescPreviewText = this.onlyIntroPreviewText;
  protected readonly propertyDescPreviewCtaText = 'Pogledajte resort';
  protected readonly eventPreviewText = this.onlyIntroPreviewText;
  protected readonly eventPreviewCtaText = 'Saznajte više';
  protected readonly voucherPreviewHeading = 'Poklon vaučer';
  protected readonly voucherPreviewText = '10% popusta na sljedeću rezervaciju u Valamar hotelima i resortima.';
  protected readonly voucherPreviewCtaText = 'Iskoristite vaučer';
  protected readonly voucherPreviewParagraph =
    'Vaučer vrijedi za rezervacije do kraja sezone. Uvjeti korištenja dostupni su na web stranici.';
  protected readonly giftVoucherPreviewHeading = 'Poklonite nezaboravan odmor';
  protected readonly giftVoucherPreviewText =
    'Darujte svojim najmilijima poklon vaučer za odmor u Valamar hotelima i resortima.';
  protected readonly giftVoucherPreviewCtaText = 'Kupite poklon vaučer';
  protected readonly conciergePreviewHeading = 'Naš concierge tim na usluzi';
  protected readonly conciergePreviewText =
    'Od rezervacije izleta do posebnih zahtjeva - naš concierge tim brine se da vaš boravak bude savršen.';
  protected readonly reservationPreviewName = 'Arba Resort, Valamar Collection';
  protected readonly reservationPreviewAddress = 'Rab bb, 51280 Rab, Hrvatska';
  protected readonly reservationPreviewTelephone = '+385 51 724 444';
  protected readonly reservationPreviewCheckIn = '15:00';
  protected readonly reservationPreviewCheckOut = '11:00';
  protected readonly checkInPreviewHeading = 'Online check-in dostupan';
  protected readonly checkInPreviewText =
    'Uštedite vrijeme po dolasku - prijavite se online najkasnije 24 sata prije početka boravka.';
  protected readonly checkInPreviewCtaText = 'Prijavite se online';
  protected readonly disclaimerPreviewText =
    'Ponuda vrijedi do isteka zaliha ili do promjene uvjeta. Cijene su izražene u eurima i uključuju PDV. Valamar Riviera d.d. zadržava pravo izmjene uvjeta ponude bez prethodne najave.';
  protected readonly valamarAppPreviewHeading = 'Preuzmite Valamar aplikaciju';
  protected readonly valamarAppPreviewText =
    'Upravljajte rezervacijom, otključajte sobu mobitelom i ostvarite posebne pogodnosti uz Valamar aplikaciju.';
  protected readonly x2PropRecPreviewCards = [
    { name: 'Arba Resort, Valamar Collection', destination: 'OTOK RAB', ctaText: 'Saznajte više' },
    { name: 'Arba Family Room', destination: 'OTOK RAB', ctaText: 'Saznajte više' },
  ];
  protected readonly propertyUspPreviewTitle = 'Arba Resort, Valamar Collection';
  protected readonly propertyUspPreviewRows = [
    'Premium all inclusive ponuda',
    'Bazeni, plaže i aktivnosti za obitelj',
    'V Level suiteovi i moderan komfor',
  ];
  protected readonly x2ExperiencesPreviewParagraph = this.introCtaPreviewText;
  protected readonly x2ExperiencesPreviewCards = [
    { heading: 'Arba Resort, Valamar Collection', description: 'Moderan odmor uz more.', ctaText: 'Saznajte više' },
    { heading: 'Arba Family Room', description: 'Obiteljski smještaj uz mnoštvo sadržaja.', ctaText: 'Saznajte više' },
  ];
  protected readonly inspirationPreviewCards = [
    { heading: 'Arba Resort, Valamar Collection', ctaText: 'Saznajte više' },
    { heading: 'Arba Family Room', ctaText: 'Saznajte više' },
  ];
  protected readonly x3ExperiencesPreviewCards = [
    { heading: 'Arba Resort, Valamar Collection', ctaText: 'Saznajte više' },
    { heading: 'Arba Family Room', ctaText: 'Saznajte više' },
    { heading: 'Bellevue, Valamar Signature', ctaText: 'Saznajte više' },
  ];
  protected readonly x2OnpPreviewCards = [
    { name: 'Arba Resort, Valamar Collection', oldPrice: '€430', newPrice: '€349', ctaText: 'Saznajte više' },
    { name: 'Arba Family Room', oldPrice: '€470', newPrice: '€384', ctaText: 'Saznajte više' },
  ];
  protected readonly reviewsPreviewName = 'Arba Resort, Valamar Collection';
  protected readonly reviewsPreviewText =
    'Izvrstan obiteljski resort, prekrasna lokacija i vrhunski sadržaji za djecu i odrasle.';
  protected readonly zigzagPreviewHeading = 'Arba Resort, Valamar Collection';
  protected readonly zigzagPreviewText =
    'Arba Resort, Valamar Collection premium je obiteljska oaza na otoku Rabu. Nudi moderan smještaj, bazene, plaže i aktivnosti za cijelu obitelj.';
  protected readonly zigzagPreviewCtaText = 'Saznajte više';
  protected readonly zigzagAltPreviewCtaText = 'Pogledajte više';

  /** The block currently shown in the large click-to-preview modal, null when closed. */
  protected previewedBlock: IBlockForSelect | null = null;
  protected previewDeviceMode: 'desktop' | 'mobile' = 'desktop';

  /** Escape closes the topmost overlay first - the large preview modal if
   *  it's open, otherwise the catalog itself. */
  @HostListener('document:keydown.escape')
  protected onEscapeKey(): void {
    if (this.previewedBlock) {
      this.closePreview();
    } else {
      this.closeModal.emit();
    }
  }

  protected setHovered(block: IBlockForSelect | null, event?: MouseEvent): void {
    this.hoveredBlock = block;
    if (block && event) {
      const row = event.currentTarget as HTMLElement;
      const modal = row.closest('.cr-add-block-modal') as HTMLElement | null;
      if (modal) {
        this.hoveredRowOffsetTop = row.getBoundingClientRect().top - modal.getBoundingClientRect().top;
      }
    }
  }

  protected previewDescription(block: IBlockForSelect): string {
    return this.realDescriptions[block.name] ?? this.placeholderDescription;
  }

  /** Only Main Banner, Intro + CTA, Big Headline, Only Intro, Only CTA,
   *  Property Description and 2x Property + Destination Name have a
   *  finished large preview so far - other rows still show the "Preview
   *  block" button (matching the catalog layout), it just doesn't open
   *  anything yet until their previews are built too. */
  protected hasRealPreview(block: IBlockForSelect): boolean {
    return (
      block.name === 'main-banner' ||
      block.name === 'paragraph-cta' ||
      block.name === 'heading' ||
      block.name === 'only-intro' ||
      block.name === 'only-cta' ||
      block.name === 'property-description' ||
      block.name === 'event' ||
      block.name === 'x2-prop-rec' ||
      block.name === 'counter' ||
      block.name === 'property-usp-1x-photo' ||
      block.name === 'property-usp-2x-photo' ||
      block.name === 'x2-experiences' ||
      block.name === 'inspiration' ||
      block.name === 'x3-experiences' ||
      block.name === 'only-2-photos' ||
      block.name === 'voucher' ||
      block.name === 'gift-voucher' ||
      block.name === 'experience-concierge' ||
      block.name === 'moments' ||
      block.name === 'reservation' ||
      block.name === 'check-in' ||
      block.name === 'disclaimer' ||
      block.name === 'valamar-app' ||
      block.name === 'x2-prop-rec-onp' ||
      block.name === 'reviews' ||
      block.name === 'zigzag-heading-and-cta' ||
      block.name === 'zigzag-heading-and-alt-cta' ||
      block.name === 'zigzag-no-heading-no-button'
    );
  }

  protected openPreview(block: IBlockForSelect): void {
    if (!this.hasRealPreview(block)) {
      return;
    }
    this.previewedBlock = block;
    this.previewDeviceMode = 'desktop';
  }

  protected closePreview(): void {
    this.previewedBlock = null;
  }

  protected setPreviewDevice(mode: 'desktop' | 'mobile'): void {
    this.previewDeviceMode = mode;
  }

  protected insertFromPreview(): void {
    if (this.previewedBlock) {
      this.selectBlock(this.previewedBlock);
      this.previewedBlock = null;
    }
  }

  /** Mirrors app-add-block's emitBlockSelect() exactly. */
  protected selectBlock(block: IBlockForSelect): void {
    const spliceIndex = this.selectedBlocks.indexOf(this.blockData) + 1;
    this.blockSelectEvent.emit({ selectedBlock: block, spliceIndex });
  }
}
